import React, { useEffect, useRef } from 'react';
import { ENV } from 'src/config/env';
import VideoResource from 'src/entities/video/VideoResource';
import videojs, { VideoJsPlayer } from 'video.js';
import 'video.js/dist/video-js.css';

interface VideoPlayerProps {
    onReady?: (VideoJsPlayer) => void;
    resource?: VideoResource;
    disableAutoplay?: boolean;
    poster?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ resource, disableAutoplay = false, onReady, poster }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<VideoJsPlayer | null>(null);

    // Setup Player
    useEffect(() => {
        // Setup only once
        if (playerRef.current) return;

        // Wait for video ref
        if (!videoRef.current) return;

        const player = (playerRef.current = videojs(
            videoRef.current,
            {
                autoplay: !disableAutoplay,
                controls: true,
                responsive: true,
                fluid: true,
                sources: resource ? [{ src: ENV.baseUrl + resource.src, type: resource.type }] : undefined,
            },
            () => {
                onReady && onReady(player);
            }
        ));
    }, [onReady, resource, videoRef, disableAutoplay]);

    // Dispose Player
    useEffect(() => {
        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [playerRef]);

    return (
        <div data-vjs-player>
            <video ref={videoRef} className="video-js vjs-big-play-centered" poster={poster} />
        </div>
    );
};

export default VideoPlayer;
